import React, { useMemo } from "react";
import clsx from "clsx";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { IconButton, Select, TextField } from "@components";
import { DatePicker } from "@components/DatePicker";
import IconTrash from "@assets/icon_trash.svg";
import IconDrag from "@assets/icon-drag.svg";
import OpenInNewIcon from "@assets/open-in-new.svg";
import { openLinkInNewTab } from "@utils";
import { dictionary } from "@constants";
import { useAppSelector } from "@hooks";
import { createCertificationNewPoint } from "@utils";
import { VENDOR_OPTIONS, fields } from "./localization";
import styles from "./CertificationsNewList.module.scss";
const parseDateValue = (value) => {
    if (!value) {
        return null;
    }
    const monthYearMatch = /^(\d{2})\/(\d{4})$/.exec(value);
    if (monthYearMatch) {
        return new Date(Number(monthYearMatch[2]), Number(monthYearMatch[1]) - 1);
    }
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};
export function CertificationsNewList({ items, onEdit }) {
    const { language, isViewOnly } = useAppSelector((state) => state.ui);
    const { isTriedToSave } = useAppSelector((state) => state.cv);
    const field = fields(language);
    const { remove } = dictionary(language);
    const datePlaceholder = language === "RU" ? "ДД/ММ/ГГГГ" : "DD/MM/YYYY";
    const vendorOptions = useMemo(() => [...VENDOR_OPTIONS]
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
        .map((option) => ({ id: option, label: option })), []);
    const updateField = (index, key, value) => {
        onEdit(items.map((item, itemIndex) => (itemIndex === index ? Object.assign(Object.assign({}, item), { [key]: value }) : item)));
    };
    const onDragEnd = (result) => {
        if (!result.destination || result.destination.index === result.source.index || isViewOnly) {
            return;
        }
        const updated = [...items];
        const [removed] = updated.splice(result.source.index, 1);
        updated.splice(result.destination.index, 0, removed);
        onEdit(updated);
    };
    const onRemove = (index) => {
        if (items.length <= 1) {
            onEdit([createCertificationNewPoint()]);
            return;
        }
        onEdit(items.filter((_, itemIndex) => itemIndex !== index));
    };
    return (React.createElement(DragDropContext, { onDragEnd: onDragEnd },
        React.createElement(Droppable, { droppableId: "certifications-new-list", type: "certifications-new" }, (provided) => (React.createElement("div", Object.assign({ ref: provided.innerRef }, provided.droppableProps, { className: styles.container }),
            items.map((item, index) => {
                const isSingleEmpty = items.length === 1 &&
                    !item.vendor &&
                    !item.name &&
                    !item.dateOfIssue &&
                    !item.expirationDate &&
                    !item.publicLink;
                return (React.createElement(Draggable, { key: item.displayId, draggableId: item.displayId, index: index, isDragDisabled: isViewOnly || items.length < 2 }, (draggableProvided, snapshot) => {
                    var _a;
                    return (React.createElement("div", Object.assign({ ref: draggableProvided.innerRef }, draggableProvided.draggableProps, { className: styles.itemRow }),
                        React.createElement("div", { className: clsx(styles.item, snapshot.isDragging && styles.isDragging) },
                            React.createElement("div", Object.assign({ className: styles.dragIconContainer }, draggableProvided.dragHandleProps),
                                React.createElement(IconDrag, { className: clsx(styles.drag, (isViewOnly || items.length < 2) && styles.dragDisabled) })),
                            React.createElement("div", { className: styles.fields },
                                React.createElement(Select, { label: field.vendor, value: item.vendor, options: vendorOptions.map((option) => (Object.assign(Object.assign({}, option), { isSelected: option.label === item.vendor }))), onOptionClick: (_, option) => {
                                        updateField(index, "vendor", option.label);
                                    }, placeholder: field.vendorPlaceholder, disabled: isViewOnly, isSelectable: true, hasInput: true, hasError: isTriedToSave && !item.vendor }),
                                React.createElement("div", { className: styles.datePickerField },
                                    React.createElement("p", { className: clsx("label-medium", styles.datePickerLabel, isTriedToSave && !item.dateOfIssue && styles.datePickerLabelError) }, field.dateOfIssue),
                                    React.createElement(DatePicker, { className: styles.datePicker, placeholderText: datePlaceholder, value: parseDateValue(item.dateOfIssue), onChange: (value) => {
                                            const nextValue = value instanceof Date ? value.toISOString() : undefined;
                                            updateField(index, "dateOfIssue", nextValue);
                                        }, disabled: isViewOnly, hasError: isTriedToSave && !item.dateOfIssue })),
                                React.createElement("div", { className: styles.fieldFullRow },
                                    React.createElement(TextField, { label: field.name, value: item.name, onChange: ({ target: { value } }) => updateField(index, "name", value), disabled: isViewOnly, maxLength: 250, hasError: isTriedToSave && !item.name.trim() })),
                                React.createElement("div", { className: styles.publicLinkRow },
                                    React.createElement(TextField, { label: field.publicLink, value: item.publicLink || "", placeholder: "URL", onChange: ({ target: { value } }) => updateField(index, "publicLink", value), disabled: isViewOnly }),
                                    React.createElement(IconButton, { className: styles.openLinkButton, icon: React.createElement(OpenInNewIcon, null), color: "secondary", disabled: !((_a = item.publicLink) === null || _a === void 0 ? void 0 : _a.trim()), onClick: () => { var _a; return openLinkInNewTab((_a = item.publicLink) !== null && _a !== void 0 ? _a : ""); }, title: field.openLink })),
                                React.createElement("div", { className: styles.datePickerField },
                                    React.createElement("p", { className: clsx("label-medium", styles.datePickerLabel) }, field.expirationDate),
                                    React.createElement(DatePicker, { className: styles.datePicker, placeholderText: datePlaceholder, value: parseDateValue(item.expirationDate), onChange: (value) => {
                                            const nextValue = value instanceof Date ? value.toISOString() : undefined;
                                            updateField(index, "expirationDate", nextValue);
                                        }, disabled: isViewOnly, allowFutureDates: true })))),
                        React.createElement("div", { className: styles.trashIconContainer },
                            React.createElement(IconButton, { icon: React.createElement(IconTrash, null), color: "secondary", className: styles.trash, onClick: () => onRemove(index), disabled: isSingleEmpty || isViewOnly, "aria-label": remove }))));
                }));
            }),
            provided.placeholder)))));
}
